import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useButton, useI18n, useLogger, useSelector } from '~Hooks';
import IconButton from '~ComponentsImproved/IconButton';
import PrinterIcon from '~Assets/icons/print.svg';
import Icon from '~ComponentsImproved/Icon';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import styled from '@emotion/styled';
import { BlackButton, BlueButton, Box, GreenButton } from '~Styles';
import PrinterConnectedIcon from '~Assets/icons/print_connect.svg';
import PrintErrorIcon from '~Assets/icons/print_error.svg';
import SignalDisconnectedIcon from '~Assets/icons/signal_disconnected.svg';
import PrintDisabledIcon from '~Assets/icons/print_disabled.svg';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { StyledLink } from './styles';
import PrinterRow from './PrinterRow';
import usePrintReceipt from '~Hooks/usePrintReceipt';
import { useStorage } from '~Components/Storage';
import InternetStatus from '~Components/InternetStatus';
import BongGroupHistory from './BongGroupHistory';
import FailedPrints from './FailedPrints';

const Content = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  padding: 32px 16px;
  width: 448px;
  background-color: var(--colorBlueGray100);
  overflow: auto;
  box-shadow: 16px 0px 32px 0px rgb(0 0 0 / 32%);
`;

export const STATUS_COLORS = {
  OK: 'var(--colorUIGreen)',
  ERROR: 'var(--colorUIRed)',
  WARNING: 'var(--colorUIOrange)',
  NOT_CONNECTED: 'var(--colorStatusGray)',
};

const STATUS_ICONS = {
  OK: PrinterConnectedIcon,
  ERROR: PrintErrorIcon,
  WARNING: SignalDisconnectedIcon,
  NOT_CONNECTED: PrintDisabledIcon,
};

export type StatusType = 'OK' | 'ERROR' | 'WARNING' | 'NOT_CONNECTED';

const ROUTES_TO_HIDE = ['customer-display', 'express'];

interface Props {
  sendTestBong?: () => void;
  hideBorder?: boolean;
  shrink?: boolean;
}

/** Displays in a slide menu the status of printers */
const PrinterStatus: FC<Props> = ({ sendTestBong, hideBorder, shrink }) => {
  const { i18n } = useI18n();

  const venueId = useSelector((state) => state.venueId);
  const isScanning = useSelector((state) => state.POSPrinterScanActive);
  const print = useSelector((state) => state.POSReceiptPrints);
  const POSOrder = useSelector((state) => print && state.POSOrders.find((o) => o.id === print.id));
  const printers = useSelector((state) => state.printers);
  const printErrors = useSelector((state) =>
    state.POSPrintQueue.filter((print) => Boolean(print.error)),
  );

  const order = POSOrder || print?.order;
  const allPrintersOnline = printers.every((printer) => printer.online);

  const showStatusModal = useModal();

  const location = useLocation();
  const logger = useLogger('printer-status');

  const [storageData] = useStorage();
  const { printReceipt } = usePrintReceipt({ venueId: venueId ?? '' });

  const [justSentBongs, setJustSentBongs] = useState<boolean>(false);
  const [status, setStatus] = useState<StatusType>('OK');

  useEffect(() => {
    if (printErrors.length) {
      setStatus('ERROR');
    } else if (!printers.length) {
      setStatus('NOT_CONNECTED');
    } else if (!allPrintersOnline) {
      setStatus('WARNING');
    } else {
      setStatus('OK');
    }
  }, [printErrors, printers, allPrintersOnline]);

  useEffect(() => {
    if (printErrors.length > 0) {
      logger.info('Printer errors: ' + printErrors.length, {
        printErrors,
      });
    }
  }, [printErrors.length]);

  const getHeadingCopy = () => {
    if (status === 'OK') {
      return i18n('PrinterStatus.Operational');
    } else if (status === 'NOT_CONNECTED') {
      return i18n('PrinterStatus.NotConnected');
    } else if (status === 'WARNING') {
      return i18n('PrinterStatus.Warning');
    } else {
      return i18n('PrinterStatus.Error');
    }
  };

  const getDescriptionCopy = () => {
    if (status === 'WARNING') {
      return i18n('PrinterStatus.Warning.Desc');
    } else if (status === 'ERROR') {
      return i18n('PrinterStatus.Error.Desc');
    } else {
      return null;
    }
  };

  const descriptionCopy = getDescriptionCopy();

  const handleClick = () => {
    showStatusModal.toggle();

    logger.info('Clicked printer status', {
      printErrors,
      printers,
    });
  };

  const reconnectButton = useButton({
    onClick: () => {
      logger.info('Running printer discover through printer status view');
      window.postMessageReact('available-printers');
    },
  });

  const manualPrintButton = useButton({
    onClick: () => {
      logger.info('Printing receipt via manual print button');
      printReceipt();
    },
  });

  const sendBongButton = useButton({
    onClick: () => {
      if (justSentBongs || !sendTestBong) {
        return;
      }

      setJustSentBongs(true);

      setTimeout(() => {
        setJustSentBongs(false);
      }, 1500);

      logger.info('Sending manual bong through printer status');
      sendTestBong();
    },
  });

  if (ROUTES_TO_HIDE.includes(location.pathname)) {
    return null;
  }

  const styles = {
    // Set border none if hideBorder is true
    ...(hideBorder ? { borderTop: 'none' } : {}),
    // Transform the icon if shrink is true
    ...(shrink ? { transform: 'scale(0.75)' } : {}),
  };

  return (
    <>
      <IconButton
        icon={<Icon src={PrinterIcon} color={STATUS_COLORS[status]} />}
        text={i18n('PrinterStatus.Title')}
        onClick={handleClick}
        styles={styles}
      />

      <Modal isShowing={showStatusModal.isShowing} hide={showStatusModal.hide}>
        <Content display="flex" flexDirection="column" gap="medium">
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            gap="small">
            <Icon
              src={STATUS_ICONS[status]}
              color={status === 'NOT_CONNECTED' ? 'var(--colorBlack)' : STATUS_COLORS[status]}
              height="80px"
              width="80px"
            />
            <Box display="flex" flexDirection="column" gap="xs" textAlign="center">
              <Heading level={1}>{getHeadingCopy()}</Heading>
              {descriptionCopy && <Paragraph level={3}>{descriptionCopy}</Paragraph>}
            </Box>

            {/* No reason to show internet status if there is not printer added yet  */}
            {status !== 'NOT_CONNECTED' && <InternetStatus />}

            {printers.length > 0 && (
              <Box fluid display="flex" flexDirection="column" textAlign="left" gap="xs">
                <Paragraph level={4}>{i18n('Printer.Status.AddedPrinters')}:</Paragraph>

                <Box fluid display="flex" flexDirection="column" gap="xs">
                  {/* List all printers and their status */}
                  {printers.map((printer) => (
                    <PrinterRow printer={printer} key={printer.id} />
                  ))}
                </Box>
              </Box>
            )}

            <StyledLink to="settings-v2/printers">
              <BlueButton fluid>{i18n('PrinterStatus.PrinterSettings')}</BlueButton>
            </StyledLink>

            {/* We hide it if no printers since we have a button further down */}
            {printers.length > 0 && (
              <>
                {sendTestBong && (
                  <BlackButton {...sendBongButton} disabled={justSentBongs} fluid>
                    {justSentBongs ? i18n('PrinterStatus.Sent') : i18n('PrinterStatus.SendBong')}
                  </BlackButton>
                )}

                {/* Run printer discover as a troubleshoot step */}
                {printErrors.length > 0 && (
                  <BlackButton {...reconnectButton} disabled={isScanning} fluid>
                    {isScanning ? i18n('PrinterStatus.Scanning') : i18n('PrinterStatus.Reconnect')}
                  </BlackButton>
                )}
              </>
            )}

            {/* If no printers are connected no need to show these buttons */}
            {printers.length > 0 && (
              <>
                {order && storageData.manualReceiptPrint && (
                  <GreenButton {...manualPrintButton} fluid>
                    {i18n('PrinterStatus.PrintReceipt')}
                  </GreenButton>
                )}
              </>
            )}

            {printErrors.length > 0 && (
              <Box marginTop="small">
                <Heading level={5}>{i18n('Printer.Status.FailedPrints')}:</Heading>
                <FailedPrints />
              </Box>
            )}
          </Box>

          {printErrors.length === 0 && printers.length !== 0 && <BongGroupHistory />}
        </Content>
      </Modal>
    </>
  );
};

export default React.memo(PrinterStatus);
